import { KeyboardEvent } from 'react'

// see https://github.com/mui/material-ui/blob/a2cd245fe41269481f9f9187ded2e807798b96dd/packages/mui-base/src/TrapFocus/TrapFocus.js

const candidatesSelector = [
  'input',
  'select',
  'textarea',
  'a[href]',
  'button',
  '[tabindex]',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',')

const getTabIndex = (node: any) => {
  const tabindexAttr = parseInt(node.getAttribute('tabindex'), 10)

  if (!Number.isNaN(tabindexAttr)) {
    return tabindexAttr
  }

  // Browsers do not return `tabIndex` correctly for contentEditable nodes;
  // https://bugs.chromium.org/p/chromium/issues/detail?id=661108&q=contenteditable%20tabindex&can=2
  // so if they don't have a tabindex attribute specifically set, assume it's 0.
  // in Chrome, <details/>, <audio controls/> and <video controls/> elements get a default
  //  `tabIndex` of -1 when the 'tabindex' attribute isn't specified in the DOM,
  //  yet they are still part of the regular tab order; in FF, they get a default
  //  `tabIndex` of 0; since Chrome still puts those elements in the regular tab
  //  order, consider their tab index to be 0.
  if (
    node.contentEditable === 'true' ||
    ((node.nodeName === 'AUDIO' || node.nodeName === 'VIDEO' || node.nodeName === 'DETAILS') &&
      node.getAttribute('tabindex') === null)
  ) {
    return 0
  }

  return node.tabIndex
}

const isNonTabbableRadio = (node: any) => {
  if (node.tagName !== 'INPUT' || node.type !== 'radio') {
    return false
  }
  if (!node.name) {
    return false
  }

  const getRadio = (selector: string) => node.ownerDocument.querySelector(`input[type="radio"]${selector}`)

  let roving = getRadio(`[name="${node.name}"]:checked`)
  if (!roving) {
    roving = getRadio(`[name="${node.name}"]`)
  }

  return roving !== node
}

const isNodeMatchingSelectorFocusable = (node: any) => {
  if (node.disabled || (node.tagName === 'INPUT' && node.type === 'hidden') || isNonTabbableRadio(node)) {
    return false
  }
  return true
}

const getTabbable = (container: any, allowNegatives: boolean = false) => {
  const regularTabNodes: Array<any> = []
  const orderedTabNodes: Array<any> = []
  Array.from(container.querySelectorAll(candidatesSelector)).forEach((node, i) => {
    const nodeTabIndex = getTabIndex(node)
    if ((!allowNegatives && nodeTabIndex === -1) || !isNodeMatchingSelectorFocusable(node)) {
      return
    }

    if (nodeTabIndex === 0) {
      regularTabNodes.push(node)
    } else {
      orderedTabNodes.push({
        documentOrder: i,
        tabIndex: nodeTabIndex,
        node,
      })
    }
  })

  // see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex
  // tabIndex=-1 > not reachable via tab navigation
  // tabIndex=0  > reachable after any positive tabindex values
  // tabIndex>0  > reachable with sequential order from lower to higher value
  return orderedTabNodes
    .sort((a, b) => (a.tabIndex === b.tabIndex ? a.documentOrder - b.documentOrder : a.tabIndex - b.tabIndex))
    .map((a) => a.node)
    .concat(regularTabNodes)
}

/**
 * set focus on first tabbable child of the container
 * including tabIndex=-1 elements
 *
 * @returns previously focused element
 */
export const grabFocus = (container: any) => {
  if (!container) return
  const previouslyFocusedElement = document.activeElement
  const tabbable = getTabbable(container, true)
  if (tabbable.length > 0) {
    tabbable[0].focus()
  } else {
    container.focus()
  }
  return previouslyFocusedElement
}

/**
 * trap keyboard tab navigation inside the child of a container
 * excluding tabIndex=-1 elements
 */
export const trapFocus = (container: any, event: KeyboardEvent) => {
  if (!container) return
  const tabbable = getTabbable(container)
  const currentIndex = tabbable.indexOf(document.activeElement)
  const lastIndex = tabbable.length - 1
  const nextIndex = currentIndex >= 0 && currentIndex < lastIndex ? currentIndex + 1 : 0
  const prevIndex = currentIndex > 0 ? currentIndex - 1 : lastIndex

  if (event.shiftKey) {
    tabbable[prevIndex].focus()
    event.preventDefault()
  } else {
    tabbable[nextIndex].focus()
    event.preventDefault()
  }
}
